import { createAsyncThunk } from '@reduxjs/toolkit';
import * as billingApi from 'api/rest/myaliro_billing';

// ==================== Subscription Middleware ====================

export const fetchSubscription = createAsyncThunk('myaliroBilling/fetchSubscription', async () => {
    const response = await billingApi.getSubscription();
    return response.data;
});

export const createSubscription = createAsyncThunk(
    'myaliroBilling/createSubscription',
    async (payload: billingApi.CreateSubscriptionPayload) => {
        const response = await billingApi.createSubscription(payload);
        return response.data;
    }
);

export const payAndCreateSubscription = createAsyncThunk(
    'myaliroBilling/payAndCreateSubscription',
    async (payload: billingApi.PayAndCreateSubscriptionPayload) => {
        const response = await billingApi.payAndCreateSubscription(payload);
        return response.data;
    }
);

export const changePackage = createAsyncThunk(
    'myaliroBilling/changePackage',
    async (payload: billingApi.ChangePackagePayload) => {
        const response = await billingApi.changePackage(payload);
        return response.data;
    }
);

export const cancelSubscription = createAsyncThunk(
    'myaliroBilling/cancelSubscription',
    async () => {
        const response = await billingApi.cancelSubscription();
        return response.data;
    }
);

// ==================== Invoice Middleware ====================

export const fetchInvoices = createAsyncThunk(
    'myaliroBilling/fetchInvoices',
    async (params?: {
        status?: string;
        from_date?: string;
        to_date?: string;
        page?: number;
        per_page?: number;
    }) => {
        const response = await billingApi.getInvoices(params);
        return response.data;
    }
);

export const fetchOutstandingInvoices = createAsyncThunk(
    'myaliroBilling/fetchOutstandingInvoices',
    async () => {
        const response = await billingApi.getOutstandingInvoices();
        return response.data;
    }
);

export const fetchInvoice = createAsyncThunk(
    'myaliroBilling/fetchInvoice',
    async (invoiceId: number) => {
        const response = await billingApi.getInvoice(invoiceId);
        return response.data;
    }
);

export const downloadInvoicePDF = createAsyncThunk(
    'myaliroBilling/downloadInvoicePDF',
    async (invoiceId: number) => {
        const response = await billingApi.downloadInvoicePDF(invoiceId);
        return response.data;
    }
);

export const payInvoice = createAsyncThunk(
    'myaliroBilling/payInvoice',
    async ({
        invoiceId,
        monerisPaymentMethodId,
    }: {
        invoiceId: number;
        monerisPaymentMethodId: string;
    }) => {
        const response = await billingApi.payInvoice(invoiceId, {
            moneris_payment_method_id: monerisPaymentMethodId,
        });
        return { invoiceId, ...response.data };
    }
);

// ==================== Payment Method Middleware ====================
// Payment method management removed - methods managed directly through Moneris API
// Frontend uses sessionStorage for temporary payment method storage

// ==================== Billing Settings Middleware ====================

export const fetchBillingSettings = createAsyncThunk(
    'myaliroBilling/fetchBillingSettings',
    async () => {
        const response = await billingApi.getBillingSettings();
        return response.data;
    }
);

export const updateBillingSettings = createAsyncThunk(
    'myaliroBilling/updateBillingSettings',
    async (payload: billingApi.UpdateBillingSettingsPayload) => {
        const response = await billingApi.updateBillingSettings(payload);
        return { settings: payload, ...response.data };
    }
);

// ==================== Usage Tracking Middleware ====================

export const fetchCurrentUsage = createAsyncThunk('myaliroBilling/fetchCurrentUsage', async () => {
    const response = await billingApi.getCurrentUsage();
    return response.data;
});

export const fetchUsageHistory = createAsyncThunk('myaliroBilling/fetchUsageHistory', async () => {
    const response = await billingApi.getUsageHistory();
    return response.data;
});

// ==================== Dispute Middleware ====================

export const createDispute = createAsyncThunk(
    'myaliroBilling/createDispute',
    async (payload: billingApi.CreateDisputePayload) => {
        const response = await billingApi.createDispute(payload);
        return response.data;
    }
);

export const fetchDisputes = createAsyncThunk('myaliroBilling/fetchDisputes', async () => {
    const response = await billingApi.getDisputes();
    return response.data;
});

export const fetchDispute = createAsyncThunk(
    'myaliroBilling/fetchDispute',
    async (disputeId: number) => {
        const response = await billingApi.getDispute(disputeId);
        return response.data;
    }
);

// ==================== Combined Data Fetch ====================

export const fetchBillingData = createAsyncThunk('myaliroBilling/fetchBillingData', async () => {
    const [subscription, outstanding, settings, usage, history] = await Promise.all([
        billingApi.getSubscription(),
        billingApi.getOutstandingInvoices(),
        billingApi.getBillingSettings(),
        billingApi.getCurrentUsage(),
        billingApi.getUsageHistory(),
    ]);

    return {
        subscription: subscription.data,
        outstandingInvoices: outstanding.data.invoices,
        settings: settings.data,
        currentUsage: usage.data,
        usageHistory: history.data.history,
    };
});
